import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SalonLandingBackground, {
  KIOSK_PAGE_KEYS,
} from '../salon/components/SalonLandingBackground';
import PageFooter from '../salon/components/PageFooter';
import {
  clearFranchiseFlowAuth,
  hasFranchiseFlowAuth,
  loginFranchiseFlow,
  validateFranchiseFlowAuth,
} from '../shared/franchiseFlowAuth';
import { enforceFranchiseGeofence, FranchiseGeofenceError } from '../shared/franchiseGeofence';
import FranchiseStoreLocationRequiredModal from '../shared/FranchiseStoreLocationRequiredModal';
import { prefetchDeviceLocation } from '../shared/deviceLocationCache';
import {
  PRESENTATION_FRANCHISE_PASSWORD,
  PRESENTATION_FRANCHISE_USERNAME,
  PRESENTATION_HUB_PATH,
} from './presentationConfig';
import { isPresentationFranchiseSession, setPresentationMode } from './presentationSession';
import '../salon/components/SalonLandingBackground.css';
import '../face-identity/styles/FaceIdentityLuxTheme.css';

export default function PresentationLoginPage() {
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(true);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [modalFranchiseName, setModalFranchiseName] = useState('');

  useEffect(() => {
    try {
      prefetchDeviceLocation();
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setError('');
      try {
        if (hasFranchiseFlowAuth()) {
          const session = await validateFranchiseFlowAuth();
          if (session && isPresentationFranchiseSession(session)) {
            setPresentationMode(true);
            if (!cancelled) navigate(PRESENTATION_HUB_PATH, { replace: true });
            return;
          }
        }
        clearFranchiseFlowAuth();
        const session = await loginFranchiseFlow({
          username: PRESENTATION_FRANCHISE_USERNAME,
          password: PRESENTATION_FRANCHISE_PASSWORD,
        });
        if (!isPresentationFranchiseSession(session)) {
          await enforceFranchiseGeofence(session);
        }
        setPresentationMode(true);
        if (!cancelled) navigate(PRESENTATION_HUB_PATH, { replace: true });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof FranchiseGeofenceError) {
          setModalFranchiseName(err.extras?.franchise_name || '');
          setLocationModalOpen(true);
          setError(err.message);
        } else {
          setError(err?.message || 'Presentation login failed. Check presentationConfig credentials.');
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return (
    <div className="fi-lux-theme">
      <SalonLandingBackground pageKey={KIOSK_PAGE_KEYS.SALON_ENTRY} />
      <main
        className="salon-landing-foreground"
        style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <div style={{ textAlign: 'center', padding: '2rem', maxWidth: 420 }}>
          <h1 style={{ marginBottom: '0.75rem' }}>Presentation</h1>
          <p style={{ opacity: 0.85 }}>
            {busy ? 'Signing in and preparing demo guest…' : 'Ready'}
          </p>
          {error ? (
            <p role="alert" style={{ color: '#b91c1c', marginTop: '1rem', fontSize: '0.95rem' }}>
              {error}
            </p>
          ) : null}
        </div>
      </main>
      <PageFooter />
      <FranchiseStoreLocationRequiredModal
        open={locationModalOpen}
        franchiseName={modalFranchiseName}
        onClose={() => setLocationModalOpen(false)}
      />
    </div>
  );
}
